/**
 * Node.js Playwright-based web crawler
 * Replaces crawl4ai Python dependency for headless browser crawling
 */

import { chromium, type Browser, type Page } from 'playwright';
import { createLogger, summarizePayload } from '../logging/index.js';

const logger = createLogger('playwright-crawler');

// Default timeout for page navigation
const DEFAULT_TIMEOUT = 30000;

// User agent to identify our crawler
const USER_AGENT =
  'Mozilla/5.0 (compatible; BlueraKnowledge/1.0; +https://github.com/blueraai/bluera-knowledge)';

export interface PlaywrightFetchResult {
  html: string;
  links: string[];
}

/**
 * Singleton browser instance for connection reuse
 */
let browserInstance: Browser | null = null;

/**
 * Get or create the browser instance
 */
async function getBrowser(): Promise<Browser> {
  if (browserInstance?.isConnected() !== true) {
    logger.debug('Launching new Chromium browser instance');
    browserInstance = await chromium.launch({
      headless: true,
    });
  }
  return browserInstance;
}

/**
 * Close the browser instance (call on cleanup)
 */
export async function closeBrowser(): Promise<void> {
  if (browserInstance?.isConnected() === true) {
    await browserInstance.close();
    browserInstance = null;
  }
}

/**
 * Fetch a URL using Playwright headless browser
 * Returns the rendered HTML and discovered links
 */
export async function fetchWithPlaywright(
  url: string,
  timeoutMs: number = DEFAULT_TIMEOUT
): Promise<PlaywrightFetchResult> {
  const startTime = Date.now();
  logger.debug({ url, timeoutMs }, 'Fetching page with Playwright');

  const browser = await getBrowser();
  const context = await browser.newContext({
    userAgent: USER_AGENT,
  });

  let page: Page | null = null;

  try {
    page = await context.newPage();

    // Navigate to the URL and wait for network idle
    await page.goto(url, {
      waitUntil: 'networkidle',
      timeout: timeoutMs,
    });

    // Get the full rendered HTML
    const html = await page.content();

    // Extract all links from the page
    const links = await page.evaluate(() => {
      const anchors = document.querySelectorAll('a[href]');
      const hrefs: string[] = [];

      anchors.forEach((anchor) => {
        const href = anchor.getAttribute('href');
        if (href !== null && href !== '') {
          try {
            // Resolve relative URLs to absolute
            const absoluteUrl = new URL(href, document.baseURI).href;
            // Only include http/https links
            if (absoluteUrl.startsWith('http://') || absoluteUrl.startsWith('https://')) {
              hrefs.push(absoluteUrl);
            }
          } catch {
            // Skip invalid URLs
          }
        }
      });

      // Deduplicate
      return [...new Set(hrefs)];
    });

    const durationMs = Date.now() - startTime;
    logger.info(
      {
        url,
        durationMs,
        linkCount: links.length,
        ...summarizePayload(html, 'raw-html', url),
      },
      'Page fetched with Playwright'
    );

    return { html, links };
  } finally {
    // Clean up the context (closes the page too)
    await context.close();
  }
}

/**
 * Check if Playwright browsers are installed
 * Fast check without launching a browser
 */
export function isPlaywrightInstalled(): boolean {
  try {
    // Try to get the chromium executable path
    // This will throw if browsers aren't installed
    const execPath = chromium.executablePath();
    logger.debug({ execPath }, 'Playwright Chromium executable found');
    return true;
  } catch {
    return false;
  }
}
